// Shared post-login session handling used by BOTH password login (Pages/Authentication/Login.js)
// and the SSO callback page (Pages/Authentication/SSOCallback.js).
//
// Centralizes the `authUser` localStorage shape, the role gate, language sync,
// org-approval handling, and role-based landing so the two auth entry points
// stay byte-for-byte identical. The actual session (HttpOnly cookies) is set by
// the backend; this only persists the profile the UI renders from.

import { hydrateActiveContext } from '../store/activeContext/actions';
import { ensureCsrfReady } from './api';

// Roles allowed to access the web portal. Kept here so password login and SSO
// apply the exact same gate.
export const ALLOWED_LOGIN_ROLES = [
  'ROLE_ADMINISTRATOR',
  'ROLE_SUPER_ADMIN',
  'ROLE_FULL_USER',
  'ROLE_REQUESTOR',
  'ROLE_HOSPITAL_ADMIN',
  'ROLE_HOD',
  'ROLE_BMD_HOD',
  'ROLE_BME',
];

/**
 * Persists the profile and routes the user after a successful authentication,
 * regardless of how the JWT/cookies were obtained (password or SSO).
 *
 * @param {object}   args
 * @param {object}   args.responseData  The login-shaped payload (from /auth/login or /auth/me).
 * @param {function} args.navigate      react-router navigate.
 * @param {object}   args.i18n          i18next instance (for language sync).
 * @param {function} args.t             translation fn (for error copy).
 * @param {function} args.onError       called with a user-facing message when the
 *                                      session is valid but access is denied
 *                                      (disallowed role / rejected org).
 * @param {function} [args.dispatch]    redux dispatch; when provided, the
 *                                      ActiveContext slice is re-snapshotted from
 *                                      the just-persisted authUser BEFORE
 *                                      navigation, so route guards don't evaluate
 *                                      the stale logged-out snapshot on their
 *                                      first render.
 * @returns {boolean} true if the user was logged in and routed; false if onError fired.
 */
export const applyLoginSuccess = async ({ responseData, navigate, i18n, t, onError, dispatch }) => {
  // Single CSRF gate for BOTH real auth entry points (password login via Login.js and
  // SSO via SSOCallback.js both route here). Wait until the XSRF cookie is committed +
  // readable before navigating into the app, so the first mutating request can't fire
  // token-less and 401. ~500ms worst case, usually instant. Callers don't await the
  // return, so navigation still happens after the gate. NOTE: page-reload-with-session
  // does NOT pass through here — that path is gated in App.js bootstrap.
  await ensureCsrfReady();
  const {
    id,
    username,
    name,
    email,
    contactNumber,
    phoneCode,
    organization,
    profileUrl,
    role,
    roles,
    permissions,
    orgApprovalStatus,
    language,
    memberships,
    activeOrganizationId,
    activeContextName,
    activeOrganizationCurrency: activeOrgCurrencyFromResponse,
    activeOrganizationDefaultWorkOrderView: activeOrgDefaultWorkOrderViewFromResponse,
    globalView,
    canEnterGlobalView,
  } = responseData || {};

  // Role gate — identical to the legacy password-login check.
  if (!ALLOWED_LOGIN_ROLES.includes(role)) {
    onError(
      t('login.unauthorized_role_access', 'Unauthorized: Your role does not have access to this system')
    );
    return false;
  }

  // Sync UI language to the user's preference.
  if (language) {
    i18n.changeLanguage(language);
  }

  // Trust the backend response: it issued the JWT with a specific
  // activeOrganizationId (or null for Global View) and tells us whether the
  // Global View entry should appear in the switcher. No client-side fallback
  // chain — a missing activeOrganizationId means Global View by design.
  const membershipList = Array.isArray(memberships) ? memberships : [];
  const isGlobalView = !!globalView;
  const resolvedActiveOrgId = isGlobalView ? null : (activeOrganizationId ?? null);

  // Currency (ISO 4217) of the active org. The backend now resolves this from the ACTIVE org
  // (activeOrganizationCurrency), so it is correct even for multi-org users whose `organization`
  // field is null. Fall back to the embedded organization.currency for older payloads /
  // single-org users. Null in Global View (mixed currency). Every currency consumer (reporting
  // widgets, Contracts, Assets, etc.) reads this instead of defaulting to USD.
  const activeOrganizationCurrency = isGlobalView
    ? null
    : (activeOrgCurrencyFromResponse
        ?? (organization && organization.id === resolvedActiveOrgId ? organization.currency : null)
        ?? null);

  // Default Work Orders list view of the active org (REACTIVE | PREVENTIVE | ALL). Null in
  // Global View; the Work Orders page falls back to REACTIVE when it is missing.
  const activeOrganizationDefaultWorkOrderView = isGlobalView
    ? null
    : (activeOrgDefaultWorkOrderViewFromResponse ?? null);

  localStorage.setItem(
    'authUser',
    JSON.stringify({
      id,
      username,
      name,
      email,
      contactNumber,
      phoneCode,
      organization,
      profileUrl,
      role,
      roles: roles || [],
      permissions: permissions || [],
      orgApprovalStatus,
      memberships: membershipList,
      activeOrganizationId: resolvedActiveOrgId,
      activeOrganizationCurrency,
      activeOrganizationDefaultWorkOrderView,
      activeContextName: activeContextName ?? null,
      isGlobalView,
      canEnterGlobalView: !!canEnterGlobalView,
    })
  );

  // Notify usePermission()/usePermissionRefresh listeners to re-read authUser from
  // localStorage now. Without this, an already-mounted route guard keeps its memoized
  // (pre-login, empty) permissions, so the post-login redirect to a permission-gated
  // route (e.g. /reactive → SERVICE_READ) bounces to /pages-404 even though the
  // permissions are present in localStorage.
  window.dispatchEvent(new Event('permissions-updated'));

  // Same staleness problem for the redux ActiveContext slice: AuthProtected reads
  // isGlobalView from the store on its first render, before ProfileMenu's
  // hydration effect has run. Re-snapshot it from the authUser we just wrote.
  if (typeof dispatch === 'function') {
    dispatch(hydrateActiveContext());
  }

  // Org approval gates.
  if (orgApprovalStatus === 'PENDING') {
    navigate('/pending-approval');
    return true;
  }
  if (orgApprovalStatus === 'REJECTED') {
    onError(
      t(
        'login.org_registration_rejected',
        'Your organization registration was not approved. Please contact support.'
      )
    );
    return false;
  }

  // Restoration: if the backend put us back into Global View, land on the
  // global dashboard. Otherwise land in the org-scoped dashboard.
  if (isGlobalView) {
    navigate('/app/global/dashboard');
    return true;
  }

  // Check for pending deep link redirect.
  const pendingDeepLink = localStorage.getItem('pendingDeepLink');
  if (pendingDeepLink) {
    localStorage.removeItem('pendingDeepLink');
    navigate(`/asset-detail/${pendingDeepLink}`);
  } else {
    navigate(role === 'ROLE_REQUESTOR' ? '/service-requests' : '/reactive');
  }
  return true;
};
